import { ShoppingBag } from "@/components/icons";
import { Skeleton, confirm, toast } from "@/components/kit";
import { cn } from "@/lib/utils";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type {
  CustomJobBoardColumnKey,
  CustomJobBoardResult,
} from "@/modules/custom-jobs/domain/types";
import type { UseQueryResult } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  useCompleteCustomJobMutation,
  useConfirmCustomJobMutation,
  useMarkReadyCustomJobMutation,
  useStartCustomJobMutation,
} from "../api/queries";
import {
  CUSTOM_JOB_BOARD_COLUMNS,
  CustomJobKanbanColumn,
} from "./CustomJobKanbanColumn";
import { getCustomJobTransitionConfirm } from "./custom-job-transition-confirm";

type Scope = "today" | "all";
type MobileColumn = CustomJobBoardColumnKey;

const MOBILE_TABS = CUSTOM_JOB_BOARD_COLUMNS.map((col) => ({
  value: col.key,
  label: col.title,
}));

type CustomJobsBoardViewProps = {
  scope: Scope;
  board: UseQueryResult<CustomJobBoardResult>;
};

export function CustomJobsBoardView({
  scope,
  board,
}: CustomJobsBoardViewProps) {
  const nav = useNavigate();
  const canManage = useActionPermission(["custom_jobs:write"]);
  const [mobileTab, setMobileTab] = useState<MobileColumn>("pending_confirm");
  const [pendingAction, setPendingAction] = useState<{
    id: string;
    action: CustomJobBoardColumnKey | "complete";
  } | null>(null);

  const confirmJob = useConfirmCustomJobMutation();
  const startJob = useStartCustomJobMutation();
  const markReady = useMarkReadyCustomJobMutation();
  const completeJob = useCompleteCustomJobMutation();

  const columns = board.data?.columns;
  const counts = board.data?.meta?.counts;

  function goToJob(id: string) {
    nav({ to: "/app/custom-jobs/$id", params: { id } });
  }

  async function runTransition(
    id: string,
    action: CustomJobBoardColumnKey | "complete",
    confirmOptions: Parameters<typeof confirm>[0],
    fn: () => Promise<unknown>,
    successMessage: string,
  ) {
    const ok = await confirm(confirmOptions);
    if (!ok) return;

    setPendingAction({ id, action });
    fn()
      .then(() => toast.success(successMessage))
      .catch((e) =>
        toast.error("ອັບເດດບໍ່ສຳເລັດ", {
          description: e instanceof Error ? e.message : undefined,
        }),
      )
      .finally(() => setPendingAction(null));
  }

  const columnProps = {
    isLoading: board.isLoading,
    canManage: !!canManage,
    onView: goToJob,
    onConfirm: canManage
      ? (id: string) =>
          runTransition(
            id,
            "pending_confirm",
            getCustomJobTransitionConfirm("confirm"),
            () => confirmJob.mutateAsync(id),
            "ຢືນຢັນແລ້ວ",
          )
      : undefined,
    onStartWork: canManage
      ? (id: string) =>
          runTransition(
            id,
            "queued",
            getCustomJobTransitionConfirm("start"),
            () => startJob.mutateAsync(id),
            "ເລີ່ມເຮັດແລ້ວ",
          )
      : undefined,
    onMarkReady: canManage
      ? (id: string) =>
          runTransition(
            id,
            "in_progress",
            getCustomJobTransitionConfirm("mark_ready"),
            () => markReady.mutateAsync(id),
            "ພ້ອມຮັບແລ້ວ",
          )
      : undefined,
    onComplete: canManage
      ? (id: string) =>
          runTransition(
            id,
            "complete",
            getCustomJobTransitionConfirm("complete"),
            () => completeJob.mutateAsync(id),
            "ສົ່ງມອບແລ້ວ",
          )
      : undefined,
    pendingAction,
  };

  if (board.isLoading) {
    return (
      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
        {CUSTOM_JOB_BOARD_COLUMNS.map((col) => (
          <Skeleton key={col.key} className="h-80 rounded-2xl" />
        ))}
      </div>
    );
  }

  const total = counts?.total ?? 0;

  if (total === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-secondary/20 border-dashed py-16 text-center">
        <ShoppingBag className="size-10 text-muted-foreground/50" />
        <p className="text-muted-foreground">
          {scope === "today" ? "ມື້ນີ້ບໍ່ມີງານສັ່ງເຮັດກຳລັງດຳເນີນ" : "ບໍ່ມີງານສັ່ງເຮັດກຳລັງດຳເນີນ"}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2 xl:hidden">
        {MOBILE_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setMobileTab(tab.value)}
            className={cn(
              "flex-1 rounded-full border px-2 py-2 text-xs transition-colors sm:text-sm",
              mobileTab === tab.value
                ? "border-secondary bg-secondary text-secondary-foreground"
                : "border-secondary/30 hover:bg-secondary/10",
            )}
          >
            {tab.label}
            <span className="ml-1 tabular-nums">
              ({counts?.[tab.value] ?? 0})
            </span>
          </button>
        ))}
      </div>

      <div className="hidden gap-4 lg:grid lg:grid-cols-2 xl:grid-cols-4">
        {CUSTOM_JOB_BOARD_COLUMNS.map((col) => (
          <CustomJobKanbanColumn
            key={col.key}
            title={col.title}
            count={counts?.[col.key] ?? 0}
            tone={col.tone}
            jobs={columns?.[col.key] ?? []}
            {...columnProps}
          />
        ))}
      </div>

      <div className="xl:hidden">
        {CUSTOM_JOB_BOARD_COLUMNS.filter((col) => col.key === mobileTab).map(
          (col) => (
            <CustomJobKanbanColumn
              key={col.key}
              title={col.title}
              count={counts?.[col.key] ?? 0}
              tone={col.tone}
              jobs={columns?.[col.key] ?? []}
              {...columnProps}
            />
          ),
        )}
      </div>
    </div>
  );
}
